import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { copyText } from './utils/clipboard';

/**
 * 公共卡片渲染组件：分拣扫描 / 核验扫码 / 装车扫码 共用。
 * 卡片布局、条码二维码生成、条码/二维码切换逻辑在此统一实现；
 * 各调用方只需把数据适配为 CardDescriptor[] 传入，差异仅在数据与状态徽章。
 */

export interface CardBadge {
    text: string;
    className: string;  // 如 status-green / status-red / status-yellow / completed / pending
}

export interface CardDescriptor {
    id: string;            // 唯一 id（用于 canvas / wrapper id）
    title: string;         // 左上标题
    titleSuffix?: string;  // 标题后缀（如去重自动追加的 /序号，单独配色，与 title 区分）
    info: string;          // 中间信息
    barcodeValue: string;  // 条码内容
    qrcodeValue: string;   // 二维码内容
    // 换标件双码：可选。附带时图片区渲染左右两列（左物流码 / 右平台码），默认关闭不影响原单码逻辑
    dualCode?: { logistics: string; platform: string };
    badges: CardBadge[];   // 状态徽章（1 个或多个）
}

export function renderCards(
    containerId: string,
    cards: CardDescriptor[],
    summary: string,
    onRefresh?: () => void,
    emptyMessage: string = '暂无数据'
) {
    const container = document.getElementById(containerId);
    if (!container) return;

    if (cards.length === 0) {
        container.innerHTML = emptyHtml(emptyMessage);
        return;
    }

    let html = `
        <div class="details-toolbar">
            <div class="details-info">
                <span>${summary}</span>
            </div>
            <div class="global-code-tabs">
                <button class="code-tab active" data-type="barcode" data-global="true">条码</button>
                <button class="code-tab" data-type="qrcode" data-global="true">二维码</button>
            </div>
            ${onRefresh ? `
            <button class="card-refresh btn btn-secondary btn-sm">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10C4 6.686 6.686 4 10 4C13.314 4 16 6.686 16 10C16 13.314 13.314 16 10 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <path d="M8 14L4 16L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                刷新
            </button>` : ''}
        </div>
        <div class="cards-grid">
    `;

    cards.forEach((card, i) => {
        const badgesHtml = card.badges
            .map(b => `<span class="detail-card-status ${b.className}">${b.text}</span>`)
            .join('');

        // canvas/wrapper id 用数组下标 i 保证唯一，避免 card.id 为空或重复时条码互相覆盖
        // 换标件（dualCode）走双码分支：图片区左右两列，左物流码(默认条码) / 右平台码(默认二维码)，全局 tab 一键切双列码型
        if (card.dualCode) {
            html += `
                <div class="detail-card dual-label">
                    <div class="detail-card-header">
                        <div class="detail-card-mark-group">
                            <span class="detail-card-mark">${card.title}</span>${card.titleSuffix ? `<span class="detail-card-mark-suffix">${card.titleSuffix}</span>` : ''}
                            <button class="barcode-copy-btn" data-id="${i}" title="复制平台码" aria-label="复制平台码">
                                <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                                    <rect x="7" y="7" width="9" height="9" rx="2" stroke="currentColor" stroke-width="1.6"/>
                                    <path d="M4 13V5.5C4 4.7 4.7 4 5.5 4H13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                </svg>
                            </button>
                        </div>
                        <span class="detail-card-weight-volume">${card.info}</span>
                        <span class="detail-card-status-group">${badgesHtml}</span>
                    </div>
                    <div class="detail-card-code dual-code">
                        <div class="dual-code-col">
                            <span class="dual-code-label">物流码</span>
                            <div class="code-wrapper code-barcode active" id="barcode-wrapper-${i}-logistics" data-kind="logistics">
                                <canvas id="canvas-${i}-logistics"></canvas>
                            </div>
                            <div class="code-wrapper code-qrcode" id="qrcode-wrapper-${i}-logistics" data-kind="logistics">
                                <canvas id="qrcode-canvas-${i}-logistics"></canvas>
                            </div>
                            <button class="dual-code-copy-btn" data-id="${i}" data-kind="logistics" title="复制物流码" aria-label="复制物流码">${card.dualCode.logistics}</button>
                        </div>
                        <div class="dual-code-col">
                            <span class="dual-code-label">平台码</span>
                            <div class="code-wrapper code-barcode" id="barcode-wrapper-${i}-platform" data-kind="platform">
                                <canvas id="canvas-${i}-platform"></canvas>
                            </div>
                            <div class="code-wrapper code-qrcode active" id="qrcode-wrapper-${i}-platform" data-kind="platform">
                                <canvas id="qrcode-canvas-${i}-platform"></canvas>
                            </div>
                            <button class="dual-code-copy-btn" data-id="${i}" data-kind="platform" title="复制平台码" aria-label="复制平台码">${card.dualCode.platform}</button>
                        </div>
                    </div>
                </div>
            `;
        } else {
            html += `
                <div class="detail-card">
                    <div class="detail-card-header">
                        <div class="detail-card-mark-group">
                            <span class="detail-card-mark">${card.title}</span>${card.titleSuffix ? `<span class="detail-card-mark-suffix">${card.titleSuffix}</span>` : ''}
                            <button class="barcode-copy-btn" data-id="${i}" title="复制条码" aria-label="复制条码">
                                <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                                    <rect x="7" y="7" width="9" height="9" rx="2" stroke="currentColor" stroke-width="1.6"/>
                                    <path d="M4 13V5.5C4 4.7 4.7 4 5.5 4H13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                                </svg>
                            </button>
                        </div>
                        <span class="detail-card-weight-volume">${card.info}</span>
                        <span class="detail-card-status-group">${badgesHtml}</span>
                    </div>
                    <div class="code-tabs" data-id="${i}">
                        <button class="code-tab active" data-type="barcode">条码</button>
                        <button class="code-tab" data-type="qrcode">二维码</button>
                    </div>
                    <div class="detail-card-code">
                        <div class="code-wrapper code-barcode active" id="barcode-wrapper-${i}">
                            <canvas id="canvas-${i}"></canvas>
                        </div>
                        <div class="code-wrapper code-qrcode" id="qrcode-wrapper-${i}">
                            <canvas id="qrcode-canvas-${i}"></canvas>
                        </div>
                    </div>
                </div>
            `;
        }
    });

    html += '</div>';
    container.innerHTML = html;

    cards.forEach((card, i) => {
        generateBarcode(container, String(i), card.barcodeValue);
        generateQRCode(container, String(i), card.qrcodeValue);
        // 换标件：左右两列各生成条码+二维码（物流码 / 平台码），供全局 tab 切双列码型
        if (card.dualCode) {
            generateBarcode(container, `${i}-logistics`, card.dualCode.logistics);
            generateQRCode(container, `${i}-logistics`, card.dualCode.logistics);
            generateBarcode(container, `${i}-platform`, card.dualCode.platform);
            generateQRCode(container, `${i}-platform`, card.dualCode.platform);
        }
    });

    bindCardCodeTabs(container);
    bindBarcodeCopy(container, cards);
    bindDualCodeCopy(container, cards);
    bindCodePreview(container);

    if (onRefresh) {
        container.querySelector('.card-refresh')?.addEventListener('click', onRefresh);
    }
}

// 清晰度系数：按高分屏 DPR 放大物理像素（夹在 [1,3] 防止 4K 屏画超大 canvas）
const CODE_DPR = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));

function generateBarcode(container: HTMLElement, id: string, value: string) {
    const canvas = container.querySelector<HTMLCanvasElement>(`#canvas-${id}`);
    if (!canvas || !value) return;
    try {
        JsBarcode(canvas, value, {
            format: 'CODE128',
            width: 2 * CODE_DPR,
            height: 60 * CODE_DPR,
            displayValue: false,
            margin: 10 * CODE_DPR
        });
        canvas.dataset.value = value;
        // 显示高回到 DPR=1 尺寸（height 60 + 上下 margin 10×2），物理像素已放大 → 高分屏更锐利
        canvas.style.height = '80px';
    } catch (error) {
        console.error('生成条形码失败:', error);
    }
}

function generateQRCode(container: HTMLElement, id: string, value: string) {
    const canvas = container.querySelector<HTMLCanvasElement>(`#qrcode-canvas-${id}`);
    if (!canvas || !value) return;
    QRCode.toCanvas(canvas, value, {
        width: 120 * CODE_DPR,
        margin: 2 * CODE_DPR,
        color: { dark: '#000000', light: '#ffffff' }
    }, (error) => {
        if (error) console.error('生成二维码失败:', error);
    });
    // CSS 锁定显示尺寸，物理像素已放大 → 高分屏更锐利
    canvas.style.width = '120px';
    canvas.style.height = '120px';
    canvas.dataset.value = value;
}

// 条码/二维码切换：作用域限定在传入容器内，各 tab 互不影响
function bindCardCodeTabs(container: HTMLElement) {
    const codeTabs = container.querySelectorAll('.code-tab');
    codeTabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const type = tab.getAttribute('data-type');
            const isGlobal = tab.getAttribute('data-global') === 'true';
            if (!type) return;

            if (isGlobal) {
                container.querySelectorAll('.code-tab').forEach(t => {
                    t.classList.toggle('active', t.getAttribute('data-type') === type);
                });
                container.querySelectorAll('.code-wrapper').forEach(wrapper => {
                    const isBarcode = wrapper.classList.contains('code-barcode');
                    wrapper.classList.toggle('active', (type === 'barcode') === isBarcode);
                });
            } else {
                const tabsContainer = tab.closest('.code-tabs');
                const id = tabsContainer?.getAttribute('data-id');
                if (!id) return;
                tabsContainer?.querySelectorAll('.code-tab').forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                container.querySelector(`#barcode-wrapper-${id}`)?.classList.toggle('active', type === 'barcode');
                container.querySelector(`#qrcode-wrapper-${id}`)?.classList.toggle('active', type === 'qrcode');
                container.querySelectorAll('.code-tab[data-global="true"]').forEach(t => {
                    t.classList.toggle('active', t.getAttribute('data-type') === type);
                });
            }
        });
    });
}

// 点击条码/二维码 canvas 展示大图预览：ESC、点击遮罩、关闭按钮均可关闭
// 换标件双码列带 data-kind（logistics/platform），预览标题据此区分"物流码/平台码"；单码卡无 data-kind，标题不变
function bindCodePreview(container: HTMLElement) {
    const canvases = container.querySelectorAll<HTMLCanvasElement>('.code-wrapper canvas');
    canvases.forEach(canvas => {
        canvas.addEventListener('click', () => {
            const wrapper = canvas.closest('.code-wrapper');
            const isBarcode = wrapper?.classList.contains('code-barcode') ?? false;
            const kind = wrapper?.getAttribute('data-kind');
            const prefix = kind === 'logistics' ? '物流码' : kind === 'platform' ? '平台码' : '';
            showCodePreview(canvas.dataset.value || '', isBarcode, `${prefix}${isBarcode ? '条码' : '二维码'}预览`);
        });
    });
}

// 预览模态框按需创建一次后复用，事件只绑定一次
function showCodePreview(value: string, isBarcode: boolean, title: string) {
    if (!value) return;
    let modal = document.getElementById('codePreviewModal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'codePreviewModal';
        modal.className = 'modal';
        modal.innerHTML = `
            <div class="modal-content code-preview-content">
                <div class="modal-header">
                    <h3 class="modal-title"></h3>
                    <button class="btn btn-ghost btn-sm modal-close" aria-label="关闭">
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div class="modal-body code-preview-body"></div>
            </div>`;
        document.body.appendChild(modal);

        const close = () => modal!.classList.remove('active');
        modal.querySelector('.modal-close')?.addEventListener('click', close);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) close();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal!.classList.contains('active')) close();
        });
    }

    modal.querySelector('.modal-title')!.textContent = title;
    const body = modal.querySelector<HTMLElement>('.code-preview-body')!;
    body.innerHTML = '';
    // 预览不复用小图位图，用大尺寸重新生成 → 大图清晰锐利
    const big = document.createElement('canvas');
    if (isBarcode) {
        JsBarcode(big, value, {
            format: 'CODE128',
            width: 8,
            height: 480,
            displayValue: false,
            margin: 40
        });
    } else {
        QRCode.toCanvas(big, value, {
            width: 960,
            margin: 4,
            color: { dark: '#000000', light: '#ffffff' }
        }, () => {});
    }
    body.appendChild(big);
    modal.classList.add('active');
}

// 左上角条码文本复制：点击按钮复制该卡片的 barcodeValue，参照 copyTestCase 的反馈风格
function bindBarcodeCopy(container: HTMLElement, cards: CardDescriptor[]) {
    const copyButtons = container.querySelectorAll<HTMLButtonElement>('.barcode-copy-btn');
    copyButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.getAttribute('data-id');
            if (!id) return;
            const card = cards[Number(id)];
            if (!card || !card.barcodeValue) return;

            copyText(card.barcodeValue).then(() => {
                const original = btn.innerHTML;
                btn.innerHTML = `
                    <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                        <path d="M6 11L9 14L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>`;
                btn.classList.add('copied');
                btn.disabled = true;
                setTimeout(() => {
                    btn.innerHTML = original;
                    btn.classList.remove('copied');
                    btn.disabled = false;
                }, 3000);
            }).catch(err => {
                console.error('复制失败:', err);
                alert('复制失败，请手动复制');
            });
        });
    });
}

// 换标件双码卡左右列复制：物流码复制 logisticsBarcode，平台码复制 platform 值；反馈风格与左上角复制一致
function bindDualCodeCopy(container: HTMLElement, cards: CardDescriptor[]) {
    container.querySelectorAll<HTMLButtonElement>('.dual-code-copy-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.getAttribute('data-id');
            const kind = btn.getAttribute('data-kind');
            if (!id) return;
            const card = cards[Number(id)];
            if (!card?.dualCode) return;
            const value = kind === 'logistics' ? card.dualCode.logistics : card.dualCode.platform;
            if (!value) return;

            copyText(value).then(() => {
                const original = btn.innerHTML;
                btn.innerHTML = '<span class="copied">已复制 ✓</span>';
                btn.classList.add('copied');
                btn.disabled = true;
                setTimeout(() => {
                    btn.innerHTML = original;
                    btn.classList.remove('copied');
                    btn.disabled = false;
                }, 3000);
            }).catch(err => {
                console.error('复制失败:', err);
                alert('复制失败，请手动复制');
            });
        });
    });
}

function emptyHtml(message: string): string {
    return `
        <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M2 7H18" stroke="currentColor" stroke-width="2"/>
                <path d="M7 2V7" stroke="currentColor" stroke-width="2"/>
                <path d="M13 2V7" stroke="currentColor" stroke-width="2"/>
            </svg>
            <p>${message}</p>
        </div>
    `;
}
